// SF5 — Bottom Performing SKUs.
//
// The lowest-revenue products that actually sold. Products with no sales are
// excluded from the ranking and counted underneath instead: with a dormant
// catalogue every row would be a zero tied with every other zero, and the order
// between them would carry no meaning.
//
// The count is split by listing status because an inactive listing sold nothing
// because nobody could buy it, which is a different problem from an active
// listing nobody chose.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FiAlertTriangle, FiInfo } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../SalesPerformance.module.css';
import SkuRankTable from './SkuRankTable';
import { ClippedWindow, count } from './format';
import { getBottomSkus } from '../services/salesPerformanceApi';

const BottomSkus = ({ window: reportingWindow, onLoadingChange }) => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;
  const { start, end } = reportingWindow || {};

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token || !start || !end) return;
    setLoading(true);
    onLoadingChange?.(true);
    setError(null);
    try {
      setData(await getBottomSkus(token, { start, end }));
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  }, [token, start, end, onLoadingChange]);

  useEffect(() => { load(); }, [load]);

  // Unmounting mid-load would otherwise leave the page's Apply button
  // stuck reporting this component as still running.
  useEffect(() => () => onLoadingChange?.(false), [onLoadingChange]);

  const rows = data?.rows || [];
  const zero = data?.zero_sales;

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>Bottom performing SKUs</h2>
        {data && (
          <span className={local.pill}>
            {rows.length} of {count(data.products_with_sales)} with sales
          </span>
        )}
      </div>
      <p className={local.muted}>
        The lowest earners that still sold, ranked by revenue ascending. Products
        that earned nothing are counted below rather than ranked, because a table
        of zeros cannot be ordered.
      </p>
      <ClippedWindow data={data} />

      {error && (
        <div className={`${local.banner} ${error.reportUnavailable ? local.bannerWarn : local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div>
            <strong>
              {error.notConnected ? 'No Amazon account connected'
                : error.reportUnavailable ? 'Data not ready' : 'Could not load the ranking'}
            </strong>
            <div>{error.message}</div>
          </div>
        </div>
      )}

      {!error && (
        <SkuRankTable
          rows={rows}
          loading={loading}
          emptyMessage="No products sold in this period."
        />
      )}

      {zero && data?.catalogue && (
        <div className={local.zeroSales}>
          <div className={local.zeroSalesHead}>
            {count(zero.active)} of your {count(data.catalogue.active)} active
            listings earned nothing in this period
          </div>
          <div className={local.zeroSalesBreakdown}>
            {count(zero.total)} of {count(data.catalogue.total)} listings in total,
            once inactive and incomplete ones are included.
            {data.products_without_revenue > 0 && (
              <> That includes {count(data.products_without_revenue)} that moved
              units but earned nothing, such as free replacements.</>
            )}
          </div>
          {/* A product that sold inside the window and went inactive inside the
              same window is NOT in the no-sales figures above — it earned. It
              does count as inactive in the catalogue line below, which is the
              one place today's status is reported. Calling that out because the
              two blocks are measured at different times and the gap between
              them is otherwise unexplained. */}
          {data.earned_by_status?.inactive > 0 && (
            <div className={local.zeroSalesBreakdown}>
              Separately, {count(data.earned_by_status.inactive)} listings earned in
              this period but are inactive today. They are not in the figures above,
              because they sold.
            </div>
          )}
          <div className={local.zeroSalesFoot}>
            Catalogue today: {count(data.catalogue.active)} active
            {' · '}{count(data.catalogue.inactive)} inactive
            {' · '}{count(data.catalogue.incomplete)} incomplete.
            Only today's status is known — Amazon serves no status history — so
            this cannot say whether a listing was active while it was selling.
          </div>
        </div>
      )}

      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default BottomSkus;
